import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "title-input-container relative" }
const _hoisted_2 = { class: "flex gap-2" }
const _hoisted_3 = {
  key: 0,
  class: "recommendations-dropdown absolute top-full left-0 right-0 bg-white border border-[#e1e5e9] rounded-lg shadow-2xl z-50 mt-2"
}
const _hoisted_4 = { class: "hot-topic-header" }
const _hoisted_5 = { class: "category-tabs-container" }
const _hoisted_6 = { class: "category-scroll-wrapper relative" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "suggestions-grid" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "suggestion-title" }
const _hoisted_11 = { class: "text-center mt-4 pb-4" }
const _hoisted_12 = { class: "keywords-container relative" }
const _hoisted_13 = { class: "flex gap-2" }
const _hoisted_14 = {
  key: 0,
  class: "ai-loading-overlay"
}

import { toRefs, ref, computed, watch } from 'vue';

import { Star, Refresh, ArrowRight, Lightning, ArrowLeft } from '@element-plus/icons-vue';
import InputTag from '@/components/inputTag.vue';
import { hotTopicSuggestions, type SuggestionItem } from '@/data/hotTopicSuggestions';
import { aiGenerateKeywords } from '@/api/api';

interface Props {
  form: {
    name: string;
    region: string;
    keywords: string[];
    wordNumber: number;
  };
  validationState: {
    name: boolean;
    region: boolean;
    keywords: boolean;
  }
}


export default /*@__PURE__*/_defineComponent({
  __name: 'firstStep',
  props: {
    form: {},
    validationState: {}
  },
  setup(__props: any) {

const props = __props;
const { form, validationState } = toRefs(props);

// 推荐标题悬浮框功能
const showRecommendations = ref(false);
const selectedCategory = ref('');
const isRefreshing = ref(false);
const currentBatchIndex = ref(0);

// 分类标签滑动功能
const categoryScrollRef = ref<HTMLElement>();
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const showScrollButton = ref(false);

// AI生成关键词功能
const isGeneratingKeywords = ref(false);

// 获取所有可用的分类
const availableCategories = computed(() => {
  return Object.keys(hotTopicSuggestions);
});


// 初始化默认分类
const initializeDefaultCategory = () => {
  if (!selectedCategory.value) {
    // 默认选择第一个分类
    const firstCategory = availableCategories.value[0];
    if (firstCategory) {
      selectedCategory.value = firstCategory;
    }
  }
};

// 切换推荐标题显示
const toggleRecommendations = () => {
  if (!showRecommendations.value) {
    initializeDefaultCategory();
    // 延迟检查是否需要显示滑动按钮
    setTimeout(() => {
      checkScrollButton();
    }, 100);
  }
  showRecommendations.value = !showRecommendations.value;
};

// 检查是否需要显示滑动按钮
const checkScrollButton = () => {
  if (!categoryScrollRef.value) return;
  
  const scrollElement = categoryScrollRef.value;
  const isOverflowing = scrollElement.scrollWidth > scrollElement.clientWidth;
  
  showScrollButton.value = isOverflowing;
  canScrollLeft.value = scrollElement.scrollLeft > 0;
  canScrollRight.value = scrollElement.scrollLeft < (scrollElement.scrollWidth - scrollElement.clientWidth);
};

// 处理滚动事件
const handleScroll = () => {
  if (!categoryScrollRef.value) return;
  
  const scrollElement = categoryScrollRef.value;
  canScrollLeft.value = scrollElement.scrollLeft > 0;
  canScrollRight.value = scrollElement.scrollLeft < (scrollElement.scrollWidth - scrollElement.clientWidth);
};

// 向左滑动
const scrollLeft = () => {
  if (!categoryScrollRef.value || !canScrollLeft.value) return;
  
  const scrollElement = categoryScrollRef.value;
  const scrollAmount = 200; // 每次滑动的距离
  
  scrollElement.scrollTo({
    left: scrollElement.scrollLeft - scrollAmount,
    behavior: 'smooth'
  });
};

// 向右滑动
const scrollRight = () => {
  if (!categoryScrollRef.value || !canScrollRight.value) return;
  
  const scrollElement = categoryScrollRef.value;
  const scrollAmount = 200; // 每次滑动的距离
  const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
  
  scrollElement.scrollTo({
    left: Math.min(scrollElement.scrollLeft + scrollAmount, maxScroll),
    behavior: 'smooth'
  });
};

// 根据选择的分类获取推荐标题
const currentSuggestions = computed(() => {
  const category = selectedCategory.value;
  let allSuggestions: SuggestionItem[] = [];
  
  if (category && hotTopicSuggestions[category]) {
    allSuggestions = hotTopicSuggestions[category];
  } else {
    allSuggestions = [
      { title: '数字化转型对现代企业管理的影响研究', category: '通用' },
      { title: '人工智能技术在各行业的应用前景', category: '通用' },
      { title: '可持续发展理念在专业实践中的体现', category: '通用' },
      { title: '创新驱动发展战略的实施路径研究', category: '通用' },
      { title: '新时代背景下的行业发展趋势分析', category: '通用' },
      { title: '数字经济时代的企业转型策略研究', category: '通用' },
      { title: '绿色发展与生态文明建设路径探索', category: '通用' },
      { title: '科技创新驱动产业升级机制研究', category: '通用' }
    ];
  }
  
  // 每批显示4个标题
  const batchSize = 4;
  const startIndex = currentBatchIndex.value * batchSize;
  const endIndex = startIndex + batchSize;
  
  return allSuggestions.slice(startIndex, endIndex);
});


// 换一批功能
const refreshSuggestions = async () => {
  isRefreshing.value = true;
  
  // 模拟刷新延迟
  setTimeout(() => {
    const category = selectedCategory.value;
    let allSuggestions: SuggestionItem[] = [];
    
    if (category && hotTopicSuggestions[category]) {
      allSuggestions = hotTopicSuggestions[category];
    } else {
      allSuggestions = [
        { title: '数字化转型对现代企业管理的影响研究', category: '通用' },
        { title: '人工智能技术在各行业的应用前景', category: '通用' },
        { title: '可持续发展理念在专业实践中的体现', category: '通用' },
        { title: '创新驱动发展战略的实施路径研究', category: '通用' },
        { title: '新时代背景下的行业发展趋势分析', category: '通用' },
        { title: '数字经济时代的企业转型策略研究', category: '通用' },
        { title: '绿色发展与生态文明建设路径探索', category: '通用' },
        { title: '科技创新驱动产业升级机制研究', category: '通用' }
      ];
    }
    
    // 计算下一批的索引，如果超出范围则回到第一批
    const batchSize = 4;
    const maxBatches = Math.ceil(allSuggestions.length / batchSize);
    currentBatchIndex.value = (currentBatchIndex.value + 1) % maxBatches;
    
    isRefreshing.value = false;
  }, 500);
};

const selectCategory = (category: string) => {
  selectedCategory.value = category;
  currentBatchIndex.value = 0; // 切换分类时重置到第一批
};

const selectSuggestion = (suggestion: SuggestionItem) => {
  form.value.name = suggestion.title;
  showRecommendations.value = false;
};

// AI生成关键词函数
const generateKeywords = async () => {
  if (isGeneratingKeywords.value) return; // 防止重复请求
  
  try {
    isGeneratingKeywords.value = true;
    const response = await aiGenerateKeywords(form.value.name.trim());
    
    // 检查响应是否成功
    if (response.data && response.data.code === 0 && Array.isArray(response.data.data)) {
      // 自动填入关键词
      form.value.keywords = response.data.data;
    } else {
      console.warn('AI生成关键词返回格式异常:', response.data);
      // 可以根据具体的错误码显示不同的提示信息
      if (response.data && response.data.msg) {
        console.warn('错误信息:', response.data.msg);
      }
    }
  } catch (error) {
    console.error('AI生成关键词失败:', error);
    // 可以在这里添加用户提示，比如显示一个错误消息
  } finally {
    isGeneratingKeywords.value = false;
  }
};

// 监听标题变化，自动生成关键词
watch(() => form.value.name, async (newTitle) => {
  if (newTitle && newTitle.trim() && (!form.value.keywords || form.value.keywords.length === 0)) {
    await generateKeywords();
  }
});


return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_el_form_item, {
      label: "提交标题",
      class: _normalizeClass([{ 'is-error': !_unref(validationState).name }, "mobile-form-item"])
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_el_input, {
              modelValue: _unref(form).name,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).name) = $event)),
              placeholder: "输入标题,如果不知道写什么标题,可以试试推荐哦~",
              class: "mobile-input flex-1",
              maxlength: "40",
              "show-word-limit": "",
              type: "text"
            }, null, 8, ["modelValue"]),
            _createVNode(_component_el_button, {
              type: "primary",
              onClick: toggleRecommendations,
              class: "recommend-btn"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_icon, { class: "mr-1" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Star))
                  ]),
                  _: 1
                }),
                _cache[4] || (_cache[4] = _createTextVNode(" 推荐标题 "))
              ]),
              _: 1
            })
          ]),
          (showRecommendations.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _cache[5] || (_cache[5] = _createElementVNode("div", { class: "flex items-center gap-2 mb-3" }, [
                    _createElementVNode("div", { class: "w-2 h-2 bg-gradient-to-r from-[#ff6b6b] to-[#ee5a24] rounded-full animate-pulse" }),
                    _createElementVNode("span", { class: "text-sm font-semibold text-[#2d3748]" }, "热门标题")
                  ], -1)),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", {
                        ref_key: "categoryScrollRef",
                        ref: categoryScrollRef,
                        class: "category-scroll flex overflow-x-auto gap-2 pb-2",
                        onScroll: handleScroll
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableCategories.value, (category) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: category,
                            onClick: _withModifiers(($event: any) => (selectCategory(category)), ["prevent"]),
                            type: "button",
                            class: _normalizeClass([{
                    'bg-gradient-to-r from-[#4299e1] to-[#3182ce] text-white shadow-md border-[#3182ce]': selectedCategory.value === category,
                    'bg-white text-[#4a5568] border-[#cbd5e0] hover:bg-[#f7fafc] hover:border-[#4299e1]': selectedCategory.value !== category
                  }, "px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200 whitespace-nowrap flex-shrink-0"])
                          }, _toDisplayString(category), 11, _hoisted_7))
                        }), 128))
                      ], 544),
                      (showScrollButton.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            onClick: _withModifiers(scrollLeft, ["prevent"]),
                            type: "button",
                            class: _normalizeClass(["scroll-btn scroll-btn-left", { 'opacity-50 cursor-not-allowed': !canScrollLeft.value }])
                          }, [
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(ArrowLeft))
                              ]),
                              _: 1
                            })
                          ], 2))
                        : _createCommentVNode("", true),
                      (showScrollButton.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 1,
                            onClick: _withModifiers(scrollRight, ["prevent"]),
                            type: "button",
                            class: _normalizeClass(["scroll-btn scroll-btn-right", { 'opacity-50 cursor-not-allowed': !canScrollRight.value }])
                          }, [
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(ArrowRight))
                              ]),
                              _: 1
                            })
                          ], 2))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentSuggestions.value, (suggestion, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: index,
                      onClick: ($event: any) => (selectSuggestion(suggestion)),
                      class: "suggestion-card cursor-pointer hover:shadow-md transition-all duration-200"
                    }, [
                      _createElementVNode("div", _hoisted_10, _toDisplayString(suggestion.title), 1)
                    ], 8, _hoisted_9))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_component_el_button, {
                    onClick: refreshSuggestions,
                    class: "refresh-btn",
                    loading: isRefreshing.value
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, { class: "mr-1" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      }),
                      _cache[6] || (_cache[6] = _createTextVNode(" 换一批 "))
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ])
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(_component_el_form_item, {
      label: "类型",
      class: _normalizeClass([{ 'is-error': !_unref(validationState).region }, "mobile-form-item"])
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_select, {
          modelValue: _unref(form).region,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).region) = $event)),
          placeholder: "请选择",
          class: "mobile-select w-full"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_option, {
              label: "国家开放大学毕业论文",
              value: "0"
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(_component_el_form_item, {
      label: "关键词",
      class: _normalizeClass([{ 'is-error': !_unref(validationState).keywords }, "mobile-form-item"])
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _createVNode(InputTag, {
              modelValue: _unref(form).keywords,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).keywords) = $event)),
              "is-invalid": !_unref(validationState).keywords,
              class: "flex-1"
            }, null, 8, ["modelValue", "is-invalid"]),
            _createVNode(_component_el_button, {
              onClick: generateKeywords,
              loading: isGeneratingKeywords.value,
              disabled: !_unref(form).name.trim(),
              class: "ai-generate-btn",
              type: "primary"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_icon, { class: "mr-1" }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(form).keywords && _unref(form).keywords.length > 0 ? _unref(Refresh) : _unref(Lightning))))
                  ]),
                  _: 1
                }),
                _createTextVNode(" " + _toDisplayString(_unref(form).keywords && _unref(form).keywords.length > 0 ? '重新生成' : 'AI生成'), 1)
              ]),
              _: 1
            }, 8, ["loading", "disabled"])
          ]),
          (isGeneratingKeywords.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, _cache[7] || (_cache[7] = [
                _createElementVNode("div", { class: "ai-loading-content" }, [
                  _createElementVNode("div", { class: "ai-loading-spinner" }),
                  _createElementVNode("span", { class: "ai-loading-text" }, "AI正在为您生成关键词...")
                ], -1)
              ])))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(_component_el_form_item, {
      label: "字数预估",
      class: "mobile-form-item"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_select, {
          modelValue: _unref(form).wordNumber,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).wordNumber) = $event)),
          placeholder: "请选择",
          class: "mobile-select w-full"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_option, {
              label: "6000字版",
              value: 6000
            }),
            _createVNode(_component_el_option, {
              label: "10000字版",
              value: 10000
            }),
            _createVNode(_component_el_option, {
              label: "20000字版",
              value: 20000
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    })
  ], 64))
}
}

})